import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import NotFound from "shared/components/UIElements/NotFound";
import MapSinglePoint from "components/shared/MapSinglePoint";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import LoginCodeField from "features/users/components/LoginCodeField";

import InputPhoneNumber from "components/ui/input/InputPhoneNumber";
import { getCurrentLanguage } from "shared/utility/lang";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE, VALIDATOR_MAX, VALIDATOR_MIN } from "shared/util/validators";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";
import { yesNoOptions } from "shared/util/types";
import { USER_TYPES } from "@routebot/constants";
import { PERMISSION_TYPES } from "@routebot/constants";
import { isInternationalTheme } from "config/ConfigUtils";

const PersonelItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  //standard code
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // context
  const { schoolOptions, personelSchoolOptions, transferregionOptions, schools, users } = useContext(StoreContext);
  const auth = useContext(AuthContext);

  // item info
  const itemType = "user";
  const itemTextType = "passenger";
  const endPointUpdate = "users";
  const endPointDelete = "users";
  const endPointCreate = "users";

  // item special states
  const [showOptionalField, setShowOptionalField] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState(() => auth?.countryCode || "TR");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

  // Pickup (geton) address states
  const [markerLocationGeton, setMarkerLocationGeton] = useState();
  const [userAddressGeton, setUserAddressGeton] = useState();
  const [addressGetonInSync, setAddressGetonInSync] = useState(false);
  const [addressGetonQuality, setAddressGetonQuality] = useState(null);

  // Dropoff (getoff) address states
  const [markerLocationGetoff, setMarkerLocationGetoff] = useState();
  const [userAddressGetoff, setUserAddressGetoff] = useState();
  const [addressGetoffInSync, setAddressGetoffInSync] = useState(false);
  const [addressGetoffQuality, setAddressGetoffQuality] = useState(null);

  const mapDataChangeHandlerGeton = (address, location, inSync, quality) => {
    setMarkerLocationGeton(location);
    setUserAddressGeton(address);
    setAddressGetonInSync(inSync);
    inputHandler("addressgeton", address, true);
    setAddressGetonQuality(quality);
  };

  const mapDataChangeHandlerGetoff = (address, location, inSync, quality) => {
    setMarkerLocationGetoff(location);
    setUserAddressGetoff(address);
    setAddressGetoffInSync(inSync);
    inputHandler("addressgetoff", address, true);
    setAddressGetoffQuality(quality);
  };

  const phoneNumberChangeHandler = (phoneNumber, countryCode, isValid) => {
    setPhoneNumber(phoneNumber || "");
    setCountryCode(countryCode);
    setIsPhoneNumberValid(isValid);
  };

  const showShift = auth.permissions[PERMISSION_TYPES.ACCESS_TO_SHIFTS];
  const showShiftChangeNotification = false;
  //const showCardId = auth.permissions[PERMISSION_TYPES.USE_CARD_ID];
  const showCardId = !isInternationalTheme();
  // const showDescription = auth.permissions[PERMISSION_TYPES.USE_DESCRIPTION];
  const showDescription = true; // it always true now
  const showTransferRegion = auth.permissions[PERMISSION_TYPES.ACCESS_TO_TRANSFERS];
  const showServistesinFeatures = auth.permissions[PERMISSION_TYPES.ACCESS_TO_SERVISTESIN];
  const stopFeaturesEnabled = false;

  const isAnyFeatureEnabled =
    showServistesinFeatures ||
    showCardId ||
    showDescription ||
    showTransferRegion ||
    showShift ||
    showShiftChangeNotification ||
    stopFeaturesEnabled;

  // formstates
  const [formState, inputHandler] = useForm(
    {
      //inputs
      name: { value: "", isValid: true },

      email: { value: "", isValid: true },
      addressgeton: { value: null, isValid: true },
      addressgetoff: { value: null, isValid: true },
      identity: { value: "", isValid: true },

      // optional inputs
      cardId: { value: null, isValid: true },
      description: { value: null, isValid: true },
      distancegeton: { value: null, isValid: true },
      getonstop: { value: null, isValid: true },
      getoffstop: { value: null, isValid: true },
      approachtogetonnotification: { value: null, isValid: true },
      shiftchangenotification: { value: null, isValid: true },
      transferregion: { value: null, isValid: true },

      //selects
      school: { value: schoolOptions.length === 1 ? schoolOptions[0].value : null, isValid: true },
    },
    true,
  );

  // formstates fetch
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentUser = users.find((user) => user.id === itemId);
      setLoadedItem(currentUser);

      // Pickup (geton) - fallback to legacy address/location
      setUserAddressGeton(currentUser?.addressgeton || currentUser?.address);
      if (currentUser?.locationgeton?.lat && currentUser?.locationgeton?.lng) {
        setMarkerLocationGeton(currentUser.locationgeton);
        setAddressGetonInSync(true);
        setAddressGetonQuality(currentUser?.addressqualitygeton);
      } else if (currentUser?.location?.lat && currentUser?.location?.lng) {
        setMarkerLocationGeton(currentUser.location);
        setAddressGetonInSync(true);
        setAddressGetonQuality(currentUser?.addressquality);
      } else {
        setMarkerLocationGeton(null);
        setAddressGetonInSync(false);
        setAddressGetonQuality(null);
      }

      // Dropoff (getoff) - fallback to legacy address/location
      setUserAddressGetoff(currentUser?.addressgetoff || currentUser?.address);
      if (currentUser?.locationgetoff?.lat && currentUser?.locationgetoff?.lng) {
        setMarkerLocationGetoff(currentUser.locationgetoff);
        setAddressGetoffInSync(true);
        setAddressGetoffQuality(currentUser?.addressqualitygetoff);
      } else if (currentUser?.location?.lat && currentUser?.location?.lng) {
        setMarkerLocationGetoff(currentUser.location);
        setAddressGetoffInSync(true);
        setAddressGetoffQuality(currentUser?.addressquality);
      } else {
        setMarkerLocationGetoff(null);
        setAddressGetoffInSync(false);
        setAddressGetoffQuality(null);
      }

      setPhoneNumber(currentUser?.phonenumber || "");
      setCountryCode(currentUser?.countryCode || auth?.countryCode || "TR");
    } catch (err) {}
    setIsItemLoaded(true);
  }, [users, itemId]);

  // standard code
  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // optional formstates fetch
  useEffect(() => {
    const currentSchool = schools.find((school) => school.id.toString() === formState.inputs.school?.value?.toString());

    if (currentSchool?.usemagneticcard) {
      setShowOptionalField(true);
    } else {
      setShowOptionalField(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.inputs.school?.value]);

  // item update
  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        // Generic fields
        usertype: USER_TYPES.PERSONEL,
        name: formState.inputs.name.value.toString().trim(),
        email: formState.inputs.email.value,
        identity: formState.inputs.identity.value,
        school: formState.inputs.school.value,

        phonenumber: phoneNumber,
        countryCode: countryCode,

        // Pickup/dropoff addresses
        addressgeton: userAddressGeton,
        addressgetoff: userAddressGetoff,
        addressqualitygeton: addressGetonQuality,
        addressqualitygetoff: addressGetoffQuality,

        // Servistesin features
        approachtogetonnotification: showServistesinFeatures
          ? formState.inputs.approachtogetonnotification.value
          : undefined,
        distancegeton: showServistesinFeatures ? formState.inputs.distancegeton.value : undefined,

        // Stop based features
        getonstop: stopFeaturesEnabled ? formState.inputs.getonstop.value : undefined,
        getoffstop: stopFeaturesEnabled ? formState.inputs.getoffstop.value : undefined,

        // Region based features - currently only for transfers
        transferregion: showTransferRegion ? formState.inputs.transferregion.value : undefined,

        // School wide features
        cardId: showCardId ? formState.inputs.cardId.value : undefined,
        description: showDescription ? formState.inputs.description.value : undefined,
        isshift: showShift ? formState.inputs?.isshift.value : undefined,
        shiftchangenotification: showShiftChangeNotification
          ? formState.inputs.shiftchangenotification.value
          : undefined,
      };

      if (markerLocationGeton) {
        infoToBeSent.locationgeton = { lat: markerLocationGeton.lat, lng: markerLocationGeton.lng };
      }
      if (markerLocationGetoff) {
        infoToBeSent.locationgetoff = { lat: markerLocationGetoff.lat, lng: markerLocationGetoff.lng };
      }

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      console.log(err);
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTextType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTextType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemTextType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container">
          <CustomSelect
            options={personelSchoolOptions}
            id="school"
            onInput={inputHandler}
            label={t("client") + " *"}
            initialValue={
              personelSchoolOptions.find((option) => option.value.toString() === loadedItem?.school.toString())
                ? personelSchoolOptions.find((option) => option.value.toString() === loadedItem?.school.toString())
                : personelSchoolOptions.length === 1
                  ? personelSchoolOptions[0]
                  : null
            }
            initialValid={isNewItem ? false : true}
          ></CustomSelect>

          <div className="horizontal-flex">
            <Input
              id="name"
              element="input"
              type="text"
              label={t("name") + " *"}
              validators={[VALIDATOR_REQUIRE()]}
              errorText={t("requireField", { item: t("name") })}
              initialValue={loadedItem?.name}
              initialValid={isNewItem ? false : true}
              onInput={inputHandler}
            />
            <InputPhoneNumber
              id="phonenumber"
              label={t("phone")}
              phoneNumber={phoneNumber}
              countryCode={countryCode}
              onChange={phoneNumberChangeHandler}
              errorText={t("phoneNumberorEmpty")}
              language={getCurrentLanguage()}
              hideLabel={auth.countryCode === countryCode}
            />
          </div>

          <MapSinglePoint
            id="personelGetonMap"
            title={t("pickUpAddress")}
            label={t("pickUpAddress")}
            initialAddress={userAddressGeton || ""}
            initialAddressInSync={addressGetonInSync}
            initialLocation={markerLocationGeton}
            initialAddressQuality={addressGetonQuality || ""}
            onInput={inputHandler}
            onMapDataChange={mapDataChangeHandlerGeton}
            isRequired={true}
          />
          <MapSinglePoint
            id="personelGetoffMap"
            title={t("dropOffAddress")}
            label={t("dropOffAddress")}
            initialAddress={userAddressGetoff || ""}
            initialAddressInSync={addressGetoffInSync}
            initialLocation={markerLocationGetoff}
            initialAddressQuality={addressGetoffQuality || ""}
            onInput={inputHandler}
            onMapDataChange={mapDataChangeHandlerGetoff}
            isRequired={true}
          />

          <div className="horizontal-flex">
            <Input
              id="email"
              element="input"
              type="text"
              label={t("email")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("requireField", { item: t("email") })}
              onInput={inputHandler}
              initialValue={loadedItem?.email || ""}
              initialValid={true}
            />
            <Input
              id="identity"
              element="input"
              type="text"
              label={t("identityNumber")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("requireField", { item: t("identityNumber") })}
              initialValue={loadedItem?.identity || ""}
              initialValid={true}
              onInput={inputHandler}
            />

            {!isNewItem && (
              <LoginCodeField loginCode={loadedItem?.loginCode} phoneNumber={phoneNumber} countryCode={countryCode} />
            )}
          </div>

          {/* {showOptionalField && isAnyFeatureEnabled && ( */}
          {isAnyFeatureEnabled && (
            <div>
              {(showCardId || showDescription) && (
                <div className="horizontal-flex">
                  {showCardId && (
                    <Input
                      id="cardId"
                      element="input"
                      type="text"
                      label={t("jobClass")}
                      validators={[VALIDATOR_NONE()]}
                      initialValue={loadedItem?.cardId || ""}
                      initialValid={true}
                      onInput={inputHandler}
                    />
                  )}

                  {showDescription && (
                    <Input
                      id="description"
                      element="input"
                      type="text"
                      label={t("identifier")}
                      validators={[VALIDATOR_NONE()]}
                      initialValue={loadedItem?.description || ""}
                      initialValid={true}
                      onInput={inputHandler}
                    />
                  )}
                </div>
              )}

              {showServistesinFeatures && (
                <div className="horizontal-flex">
                  <CustomSelect
                    options={yesNoOptions}
                    id="approachtogetonnotification"
                    onInput={inputHandler}
                    initialValue={
                      yesNoOptions.find((option) => option.value === loadedItem?.approachtogetonnotification) ||
                      yesNoOptions[0]
                    }
                    label={t("approachtogetonnotification")}
                  ></CustomSelect>

                  <Input
                    id="distancegeton"
                    element="input"
                    type="text"
                    label={t("distanceBeforeGetOn")}
                    validators={[VALIDATOR_MIN(0.1), VALIDATOR_MAX(5)]}
                    initialValue={loadedItem?.distancegeton || "2"}
                    initialValid={true}
                    onInput={inputHandler}
                    errorText={t("distanceBeforeGetOnErrorText")}
                  />
                </div>
              )}

              {(showTransferRegion || showShift) && (
                <div className="horizontal-flex">
                  {showShift && (
                    <CustomSelect
                      options={yesNoOptions}
                      id="isshift"
                      onInput={inputHandler}
                      initialValue={
                        yesNoOptions.find((option) => option.value === loadedItem?.isshift) || yesNoOptions[1]
                      }
                      label={t("isshift")}
                    ></CustomSelect>
                  )}

                  {showTransferRegion && (
                    <CustomSelect
                      options={transferregionOptions}
                      id="transferregion"
                      onInput={inputHandler}
                      initialValue={
                        transferregionOptions.find((option) => option.value === loadedItem?.transferregion) || null
                      }
                      label={t("transferregion.title")}
                    ></CustomSelect>
                  )}
                </div>
              )}

              {/* <CustomSelect
                options={stopOptions}
                id="getonstop"
                onInput={inputHandlerOptional}
                label={t("getonstop")}
                validators={[VALIDATOR_NONE()]}
                initialValid={true}
                initialValue={
                  stopOptions?.find((option) => option?.value === loadedItem?.getonstop?.toString()) || null
                }
              ></CustomSelect>

              <CustomSelect
                options={stopOptions}
                id="getoffstop"
                onInput={inputHandlerOptional}
                label={t("getoffstop")}
                validators={[VALIDATOR_NONE()]}
                initialValid={true}
                initialValue={
                  stopOptions?.find((option) => option?.value === loadedItem?.getoffstop?.toString()) || null
                }
              ></CustomSelect> */}

              {/* <CustomSelect
                options={yesNoOptions}
                id="shiftchangenotification"
                onInput={inputHandlerOptional}
                initialValue={yesNoOptions.find((option) => option.value === loadedItem?.shiftchangenotification)}
                label={t("shiftchangenotification")}
              ></CustomSelect> */}
            </div>
          )}

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={
              !formState.isValid ||
              !formState.inputs.school.value ||
              !addressGetonInSync ||
              !addressGetoffInSync ||
              !isPhoneNumberValid ||
              isLoading
            }
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default PersonelItem;
